import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer({
  isMobile = false,
  onLinkClick
}: {
  isMobile?: boolean;
  onLinkClick?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="4702" className={cn("flex gap-4", isMobile ? "flex-col" : "flex-row")}>
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-5 w-20 animate-pulse bg-muted" />)}
      </div>;
  }
  if (isMobile) {
    return <nav className="flex flex-col space-y-4">
        {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium text-foreground uppercase tracking-wide transition-colors hover:text-primary" onClick={onLinkClick}>
            {item.label}
          </Link>)}
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-12">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-[12px] font-medium text-foreground/80 uppercase tracking-widest font-sans transition-colors hover:text-foreground">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[var(--luxury-gold)] transition-all duration-500 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300", isScrolled ? "bg-background/80 backdrop-blur-[12px]" : "bg-background border-b border-border/50")}>
      <div className="container mx-auto flex flex-col items-center justify-start px-4 md:px-6 h-auto relative">
        
<div className="w-full flex items-center justify-between gap-6 py-4">
  <Link to="/" className="group flex items-center gap-2 mx-auto">
    <WvcLogo className="w-auto [&_img]:w-auto [&_img]:!h-[25px] md:[&_img]:!h-[62px] [&_img]:!max-h-none [&_svg]:w-auto [&_svg]:!h-[25px] md:[&_svg]:!h-[62px] [&_svg]:!max-h-none" />
  </Link>


</div>
        
<div className="w-full flex justify-end items-center gap-6 py-4">
         
    <Button asChild className="bg-white text-black text-[11px] font-bold uppercase tracking-[0.15em] hover:bg-primary hover:text-primary-foreground hover:shadow-md transition-all duration-300 border border-black/10 rounded-lg px-5 py-2.5">
      <Link to="tel:+1234567890">Call Now</Link>
    </Button>

    <Button asChild className="bg-black text-white text-[12px] font-bold uppercase tracking-widest hover:bg-[var(--luxury-gold)] hover:text-white hover:shadow-lg transition-all duration-300 border-none">
      <Link to="/ContactUsPage">
        <span className="sm:hidden">Get Quote</span>
        <span className="hidden sm:inline">Get Free Quote</span>
      </Link>
    </Button>
  
  </div>
        
        
        <div className="hidden md:flex flex-col items-center justify-center py-4 w-full border-t border-border/20">
          <div className="w-full flex items-center justify-center gap-12">
            <MenuProvider menu_id="46">
              <HeaderMenuConsumer />
            </MenuProvider>
            
            <Button asChild className="bg-black text-white text-[12px] font-bold uppercase tracking-widest hover:bg-[var(--luxury-gold)] hover:shadow-lg transition-all duration-300 border-none">
              
            </Button>
          </div>
        </div>

        
        <div className="md:hidden absolute right-4 top-1/2 -translate-y-1/2">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" className="hover:bg-accent/50 hover:text-accent-foreground transition-colors">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      
      {isMobileMenuOpen && <div className="absolute left-0 top-full w-full border-b border-border bg-background p-4 shadow-lg md:hidden animate-in slide-in-from-top-5">
          <div className="flex flex-col space-y-6">
            <MenuProvider menu_id="46">
              <HeaderMenuConsumer isMobile onLinkClick={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            <div className="pt-4 border-t border-border">
              <Button asChild className="w-full bg-black text-white text-[12px] font-bold uppercase tracking-widest hover:bg-[var(--luxury-gold)] transition-colors">
                <Link to="/ContactUsPage" onClick={() => setIsMobileMenuOpen(false)}>
                  Get a Quote
                </Link>
              </Button>
            </div>
          </div>
        </div>}
    </section>;
}